import { effect, inject, Injectable, signal } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  merge,
  of,
  takeWhile,
  throttleTime,
  timer,
} from 'rxjs';
import { BannerService } from '@services/banner.service';
import { BuildStampApiService } from 'src/app/api/services/build-stamp-api.service';
import { environment } from 'src/environments/environment';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const ROUTE_CHECK_THROTTLE_MS = 30 * 1000;
/** Set by the backend from System Configuration; absent while the banner switch is off. */
export const REQUIRED_VERSION_HEADER = 'X-App-Required-Version';

/** Date versions `YYYY.M.D` (package.json), compared segment by segment. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d;
  }
  return 0;
}

/**
 * New-build detection (WAP-1816). A tab is told to reload when all three hold:
 * the backend requires a version newer than this bundle (System Configuration,
 * delivered as a response header, absent while the switch is off), the
 * deployed bundle satisfies that requirement (build stamp poll: start, every
 * few minutes, route change), and the tab has not been told yet.
 */
@Injectable({ providedIn: 'root' })
export class BuildStampService {
  private readonly api = inject(BuildStampApiService);
  private readonly router = inject(Router);
  private readonly banners = inject(BannerService);
  private readonly translate = inject(TranslateService);

  private readonly requiredVersion = signal<string | null>(null);
  private readonly deployedVersion = signal<string | null>(null);
  private announced = false;

  constructor() {
    effect(() => {
      const required = this.requiredVersion();
      const deployed = this.deployedVersion();
      if (this.announced || !required || !deployed) return;
      if (compareVersions(required, environment.version) <= 0) return;
      if (compareVersions(deployed, required) < 0) return;
      this.announced = true;
      this.showBanner();
    });
  }

  start(): void {
    const routeChanges$ = this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      throttleTime(ROUTE_CHECK_THROTTLE_MS),
    );
    merge(timer(0, POLL_INTERVAL_MS), routeChanges$)
      .pipe(
        takeWhile(() => !this.announced),
        exhaustMap(() =>
          this.api.getStamp().pipe(
            map((stamp) =>
              typeof stamp?.version === 'string' ? stamp.version : null,
            ),
            catchError(() => of(null)),
          ),
        ),
      )
      .subscribe((version) => {
        if (version) this.deployedVersion.set(version);
      });
  }

  /** Called by the HTTP interceptor for every API response. */
  applyResponseHeaders(headers: HttpHeaders): void {
    this.requiredVersion.set(headers.get(REQUIRED_VERSION_HEADER) || null);
  }

  /** The reload, and What's new opening the release notes browser pinned to the required version (WAP-1817, WAP-1818). */
  private showBanner(): void {
    this.banners.info(
      this.translate.instant(
        'new-build-banner.message.a-new-version-of-the-app-is-available',
      ),
      {
        variant: 'bar',
        sticky: true,
        scope: 'new-build',
        ctAs: [
          {
            type: 'primary',
            i18nKey: 'new-build-banner.button.reload',
            action: 'page:refresh',
          },
          {
            type: 'secondary',
            i18nKey: 'new-build-banner.button.whats-new',
            action: 'release-notes:open',
            meta: { version: this.requiredVersion() },
          },
        ],
      },
    );
  }
}
